import { createSlice } from "@reduxjs/toolkit";
import {
  addSaga,
  createAsyncSaga,
  createSagaReducer,
  sagaSlice,
} from "./sagaStore";
import categoriesAndSubcategoriesService from "../services/fetchCategoriesAndSubcategories";

// Response shape under result.data: a map of top level category -> subcategory list
export interface CategoriesAndSubcategoriesData {
  [category: string]: string[];
}

export interface CategoriesAndSubcategoriesState {
  list: {
    loading: boolean;
    error: string | null;
    data: CategoriesAndSubcategoriesData | null;
  };
}

const initialState: CategoriesAndSubcategoriesState = {
  list: {
    loading: false,
    error: null,
    data: null,
  },
};

// Intentionally not persisted: the category list is shop specific and refetched
// whenever it is needed so we always reflect the currently selected shop.
const Slice: sagaSlice = createSlice({
  name: "categoriesAndSubcategories",
  initialState,
  reducers: {
    ...createSagaReducer("fetchCategoriesAndSubcategories", "list", {
      success: (state: CategoriesAndSubcategoriesState, action: any) => {
        // Extract the nested data structure: response.data.result.data
        const nestedData = action?.payload?.result?.data;
        state.list.data = nestedData || null;
      },
    }),
    // Directly set the category map without the loading/clear cycle of the
    // async fetch. Used by the stock-manager cached path (see
    // utils/allowedCategories) so a cache hit can populate the store without
    // blanking the existing data or triggering a network request.
    setCategoriesAndSubcategories: (
      state: CategoriesAndSubcategoriesState,
      action: { type: string; payload: CategoriesAndSubcategoriesData | null }
    ) => {
      state.list.loading = false;
      state.list.error = null;
      state.list.data = action.payload || null;
    },
  },
});

const fetchCategoriesAndSubcategoriesSaga = createAsyncSaga(
  "fetchCategoriesAndSubcategories",
  Slice,
  categoriesAndSubcategoriesService.fetchCategoriesAndSubcategories,
  ["shopId"]
);

addSaga(
  Slice.actions.fetchCategoriesAndSubcategories.type,
  fetchCategoriesAndSubcategoriesSaga
);

export const { fetchCategoriesAndSubcategories, setCategoriesAndSubcategories } =
  Slice.actions;

export default Slice.reducer;
